// NEW_FILE_JUSTIFIED: Custom hook to encapsulate WhatsApp connect state machine (3 useState requires extraction per project rules)
import { useState, useCallback, useEffect, useRef } from 'react';
import { useQuery, useAction } from 'convex/react';
import { api } from '../../convex/_generated/api'; // DEEP_IMPORT_JUSTIFIED: _generated is the only allowed convex import from src/hooks
import type { TWhatsAppConnectStep } from '@/@types/whatsapp';

const POLL_INTERVAL_MS = 3000;
const POLL_TIMEOUT_MS = 120000;

export function useWhatsAppConnect() {
  const session = useQuery(api.whatsapp.queries.getMySession, {});
  const initiateConnectAction = useAction(api.whatsapp.sessions.initiateConnect);
  const disconnectAction = useAction(api.whatsapp.sessions.disconnect);
  const checkStatusAction = useAction(api.whatsapp.sessions.checkConnectionStatus);

  const [step, setStep] = useState<TWhatsAppConnectStep>('disconnected');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [qrCode, setQrCode] = useState<string | null>(null);

  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  // Sync with reactive session status
  useEffect(() => {
    if (session?.status === 'active') {
      setStep('connected');
      setQrCode(null);
      if (pollRef.current) clearInterval(pollRef.current);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    }
  }, [session?.status]);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const startPolling = useCallback(() => {
    stopPolling();

    let consecutiveErrors = 0;

    pollRef.current = setInterval(async () => {
      try {
        const result = await checkStatusAction({});
        consecutiveErrors = 0;
        // Only use poll for QR code retrieval.
        // Connection status is detected via the reactive getMySession query
        // (handled by the useEffect that watches session?.status).
        if (result.qr) {
          setQrCode(result.qr);
          setStep('awaiting_qr');
          // Clear any transient error from a 515 reconnect — new QR means bridge recovered
          setErrorMessage(null);
        } else if (!result.connected) {
          // Bridge has no QR (socket reconnecting after 515) — clear stale QR from React state
          // so user doesn't try to scan a dead QR code
          setQrCode(null);
        }
      } catch (err) {
        consecutiveErrors++;
        if (consecutiveErrors >= 3) {
          stopPolling();
          setErrorMessage(err instanceof Error ? err.message : 'Unable to reach WhatsApp service');
          setStep('disconnected');
        }
      }
    }, POLL_INTERVAL_MS);

    // Stop polling after timeout
    timeoutRef.current = setTimeout(() => {
      stopPolling();
      setQrCode(null);
      setStep('expired');
    }, POLL_TIMEOUT_MS);
  }, [checkStatusAction, stopPolling]);

  const handleConnect = useCallback(async () => {
    setErrorMessage(null);
    setStep('connecting');
    try {
      await initiateConnectAction({});
      startPolling();
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : 'Failed to start WhatsApp connection');
      setStep('disconnected');
    }
  }, [initiateConnectAction, startPolling]);

  const handleDisconnect = useCallback(async () => {
    setErrorMessage(null);
    stopPolling();
    try {
      await disconnectAction({});
      setStep('disconnected');
      setQrCode(null);
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : 'Disconnect failed');
    }
  }, [disconnectAction, stopPolling]);

  const isConnected = session?.status === 'active' || step === 'connected';

  return {
    session,
    step,
    errorMessage,
    qrCode,
    isConnected,
    handleConnect,
    handleDisconnect,
  };
}
